import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  collection,
  getDocs,
  getCountFromServer,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import {
  FaChartBar,
  FaTimes,
  FaCircle,
  FaTrophy,
  FaBed,
  FaClock,
  FaSignInAlt,
  FaUsers,
  FaChevronDown,
  FaChevronUp,
  FaDesktop,
  FaMobileAlt,
  FaSpinner,
  FaSort,
  FaSortUp,
  FaSortDown
} from 'react-icons/fa';
import styles from './UsageStatsModal.module.css';

const DAY_MS = 24 * 60 * 60 * 1000;
const RANGE_DAYS = 30;
const CHART_DAYS = 14;

const toMillis = (v) => {
  if (!v) return null;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  return null;
};

const formatDuration = (ms) => {
  if (!ms || ms <= 0) return '—';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h} sa ${m} dk`;
  if (m > 0) return `${m} dk`;
  return `${Math.max(1, Math.floor(ms / 1000))} sn`;
};

const formatRelative = (ms) => {
  if (!ms) return 'Hiç aktivite yok';
  const diff = Date.now() - ms;
  if (diff < 60000) return 'Az önce';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} dk önce`;
  if (diff < DAY_MS) return `${Math.floor(diff / 3600000)} saat önce`;
  return `${Math.floor(diff / DAY_MS)} gün önce`;
};

const formatDateTime = (ms) =>
  ms
    ? new Date(ms).toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    : '—';

const parseDevice = (ua) => {
  if (!ua) return { label: 'Bilinmeyen cihaz', isMobile: false };
  const isMobile = /Mobile|Android|iPhone|iPad/i.test(ua);
  let browser = 'Tarayıcı';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/OPR\//.test(ua)) browser = 'Opera';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua)) browser = 'Safari';
  let os = '';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/iPhone|iPad/.test(ua)) os = 'iOS';
  else if (/Mac OS/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/Linux/.test(ua)) os = 'Linux';
  return { label: os ? `${browser} · ${os}` : browser, isMobile };
};

const initials = (emp) =>
  `${(emp.eName || '').charAt(0)}${(emp.eSurname || '').charAt(0)}`.toUpperCase() || '?';

// LoginPage ile aynı kontrol: yalnızca aktif çalışanlar giriş yapabilir,
// istatistiklerde de yalnızca onlar gösterilir
const isActiveEmployee = (emp) => Number(emp.Active) === 1;

const fullName = (emp) => `${emp.eName || ''} ${emp.eSurname || ''}`.trim() || '—';

const UsageStatsModal = ({ employees, onlineUsers, isDarkMode, onClose }) => {
  const [historyById, setHistoryById] = useState(null); // null = loading
  const [fetchError, setFetchError] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [sortKey, setSortKey] = useState('logins30');
  const [sortDir, setSortDir] = useState('desc');
  const [expandedId, setExpandedId] = useState(null);

  const activeEmployees = useMemo(() => employees.filter(isActiveEmployee), [employees]);

  // Login geçmişi modal açılışında bir kez çekilir; canlı durum (çevrimiçi,
  // aktif oturum süresi) her render'da employees prop'undan hesaplanır.
  const employeesRef = useRef(activeEmployees);
  useEffect(() => {
    let cancelled = false;

    const fetchHistory = async () => {
      const since = new Date(Date.now() - RANGE_DAYS * DAY_MS);
      try {
        const entries = await Promise.all(
          employeesRef.current.map(async (emp) => {
            const historyRef = collection(db, 'Employees', emp.id, 'loginHistory');
            const [recentSnap, countSnap] = await Promise.all([
              getDocs(
                query(historyRef, where('timestamp', '>=', since), orderBy('timestamp', 'desc'))
              ),
              getCountFromServer(historyRef)
            ]);
            const recent = recentSnap.docs.map((d) => {
              const data = d.data();
              return { ts: toMillis(data.timestamp), device: data.device || '' };
            });
            const weekAgo = Date.now() - 7 * DAY_MS;
            return [
              emp.id,
              {
                recent,
                logins30: recent.length,
                logins7: recent.filter((r) => r.ts && r.ts >= weekAgo).length,
                totalLogins: countSnap.data().count,
                lastHistoryTs: recent.length > 0 ? recent[0].ts : null
              }
            ];
          })
        );
        if (!cancelled) setHistoryById(Object.fromEntries(entries));
      } catch (error) {
        console.error('Error fetching usage stats:', error);
        if (!cancelled) {
          setFetchError(true);
          setHistoryById({});
        }
      }
    };

    fetchHistory();
    return () => {
      cancelled = true;
    };
  }, []);

  const loading = historyById === null;

  // Çalışan başına birleşik istatistik satırları
  const rows = useMemo(() => {
    if (loading) return [];
    return activeEmployees.map((emp) => {
      const h = historyById[emp.id] || {
        recent: [],
        logins30: 0,
        logins7: 0,
        totalLogins: 0,
        lastHistoryTs: null
      };
      const loginTime = toMillis(emp.loginTime);
      const logoutTime = toMillis(emp.logoutTime);
      const lastLogin = toMillis(emp.lastLogin);
      // sessionStartTime oturum başında bir kez yazılır; loginTime her sayfa
      // geçişinde tazelendiği için oturum süresi hesabında ilki tercih edilir
      const sessionStart = toMillis(emp.sessionStartTime) || loginTime;
      const isOnline = onlineUsers.has(emp.id);

      // Login geçmişine yazılmamış girişleri (ör. geçmişte kamera izni
      // olmadığında kayıt atlanıyordu) oturum alanlarından türet
      const entries = [...h.recent];
      const DEDUP_MS = 45 * 60 * 1000;
      const since30 = Date.now() - RANGE_DAYS * DAY_MS;
      [lastLogin, sessionStart].forEach((ts) => {
        if (!ts || ts < since30) return;
        if (entries.some((e) => e.ts && Math.abs(e.ts - ts) < DEDUP_MS)) return;
        entries.push({ ts, device: '', synthetic: true });
      });
      entries.sort((a, b) => (b.ts || 0) - (a.ts || 0));
      const weekAgo = Date.now() - 7 * DAY_MS;

      // Nabız (lastSeen) en güncel aktivite işareti: sekmeyi kapatarak çıkan
      // kullanıcılarda logoutTime hiç yazılmadığı için tek kaynak odur.
      const lastSeen = toMillis(emp.lastSeen);
      const lastActivity = Math.max(
        loginTime || 0,
        logoutTime || 0,
        lastSeen || 0,
        lastLogin || 0,
        h.lastHistoryTs || 0
      ) || null;

      // Oturumun bittiği an: açık çıkış varsa o, yoksa son nabız.
      const sessionEnd = Math.max(logoutTime || 0, lastSeen || 0) || null;
      const totalOnlineMs = typeof emp.totalOnlineMs === 'number' ? emp.totalOnlineMs : 0;
      const sessionCount = typeof emp.sessionCount === 'number' ? emp.sessionCount : 0;
      return {
        emp,
        ...h,
        recent: entries,
        logins30: entries.length,
        logins7: entries.filter((e) => e.ts && e.ts >= weekAgo).length,
        totalLogins: Math.max(h.totalLogins, entries.length),
        isOnline,
        lastActivity,
        currentSessionMs: isOnline && sessionStart ? Date.now() - sessionStart : null,
        lastSessionMs:
          !isOnline && sessionStart && sessionEnd && sessionEnd >= sessionStart
            ? sessionEnd - sessionStart
            : null,
        totalOnlineMs,
        avgSessionMs: sessionCount > 0 ? totalOnlineMs / sessionCount : null
      };
    });
  }, [activeEmployees, historyById, onlineUsers, loading]);

  // Özet kutuları
  const summary = useMemo(() => {
    const active30 = rows.filter((r) => r.logins30 > 0);
    return {
      onlineCount: rows.filter((r) => r.isOnline).length,
      active30Count: active30.length,
      totalLogins30: rows.reduce((sum, r) => sum + r.logins30, 0),
      totalOnlineMs: rows.reduce((sum, r) => sum + r.totalOnlineMs, 0)
    };
  }, [rows]);

  // Son 14 günün günlük giriş sayıları (grafik için)
  const dailyChart = useMemo(() => {
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = CHART_DAYS - 1; i >= 0; i--) {
      const dayStart = today.getTime() - i * DAY_MS;
      days.push({
        start: dayStart,
        label: new Date(dayStart).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }),
        weekday: new Date(dayStart).toLocaleDateString('tr-TR', { weekday: 'short' }),
        count: 0
      });
    }
    rows.forEach((r) => {
      r.recent.forEach((entry) => {
        if (!entry.ts) return;
        const day = days.find((d) => entry.ts >= d.start && entry.ts < d.start + DAY_MS);
        if (day) day.count += 1;
      });
    });
    const max = Math.max(1, ...days.map((d) => d.count));
    return { days, max };
  }, [rows]);

  const topActive = useMemo(
    () =>
      [...rows]
        .filter((r) => r.logins30 > 0)
        .sort((a, b) => b.logins30 - a.logins30 || b.totalOnlineMs - a.totalOnlineMs)
        .slice(0, 5),
    [rows]
  );

  const leastActive = useMemo(
    () =>
      [...rows]
        .filter((r) => r.logins30 === 0)
        .sort((a, b) => (a.lastActivity || 0) - (b.lastActivity || 0))
        .slice(0, 5),
    [rows]
  );

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (sortKey === 'name') return dir * fullName(a.emp).localeCompare(fullName(b.emp), 'tr');
      const av = a[sortKey] || 0;
      const bv = b[sortKey] || 0;
      return dir * (av - bv);
    });
  }, [rows, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' ? 'asc' : 'desc');
    }
  };

  const sortIcon = (key) => {
    if (sortKey !== key) return <FaSort className={styles.sortIconIdle} />;
    return sortDir === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  return (
    <div
      className={`${styles.overlay} ${isDarkMode ? styles.darkMode : ''}`}
      onClick={onClose}
    >
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaChartBar className={styles.modalTitleIcon} />
            Kullanım İstatistikleri
          </h2>
          <button className={styles.modalClose} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'overview' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Genel Bakış
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'users' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Kullanıcı Detayları
          </button>
        </div>

        <div className={styles.modalBody}>
          {loading ? (
            <div className={styles.loadingState}>
              <FaSpinner className={styles.spinner} />
              Giriş kayıtları yükleniyor...
            </div>
          ) : (
            <>
              {fetchError && (
                <div className={styles.errorBanner}>
                  Giriş geçmişi yüklenirken bir hata oluştu, gösterilen veriler eksik olabilir.
                </div>
              )}

              {activeTab === 'overview' && (
                <>
                  <div className={styles.tiles}>
                    <div className={styles.tile}>
                      <FaCircle className={`${styles.tileIcon} ${styles.tileIconOnline}`} />
                      <div>
                        <span className={styles.tileValue}>{summary.onlineCount}</span>
                        <span className={styles.tileLabel}>Şu An Çevrimiçi</span>
                      </div>
                    </div>
                    <div className={styles.tile}>
                      <FaUsers className={styles.tileIcon} />
                      <div>
                        <span className={styles.tileValue}>
                          {summary.active30Count}
                          <small> / {rows.length}</small>
                        </span>
                        <span className={styles.tileLabel}>Aktif Kullanıcı (30 gün)</span>
                      </div>
                    </div>
                    <div className={styles.tile}>
                      <FaSignInAlt className={styles.tileIcon} />
                      <div>
                        <span className={styles.tileValue}>{summary.totalLogins30}</span>
                        <span className={styles.tileLabel}>Toplam Giriş (30 gün)</span>
                      </div>
                    </div>
                    <div className={styles.tile}>
                      <FaClock className={styles.tileIcon} />
                      <div>
                        <span className={styles.tileValue}>
                          {formatDuration(summary.totalOnlineMs)}
                        </span>
                        <span className={styles.tileLabel}>Toplam Çevrimiçi Süre</span>
                      </div>
                    </div>
                  </div>

                  <div className={styles.sectionTitle}>Günlük Girişler (Son {CHART_DAYS} Gün)</div>
                  <div className={styles.chart}>
                    {dailyChart.days.map((d) => (
                      <div key={d.start} className={styles.chartCol} title={`${d.label}: ${d.count} giriş`}>
                        <span className={styles.chartCount}>{d.count > 0 ? d.count : ''}</span>
                        <div className={styles.chartBarTrack}>
                          <div
                            className={styles.chartBar}
                            style={{ height: `${(d.count / dailyChart.max) * 100}%` }}
                          />
                        </div>
                        <span className={styles.chartLabel}>{d.label}</span>
                        <span className={styles.chartWeekday}>{d.weekday}</span>
                      </div>
                    ))}
                  </div>

                  <div className={styles.rankings}>
                    <div className={styles.rankingCard}>
                      <div className={styles.rankingTitle}>
                        <FaTrophy className={styles.rankingIconTop} />
                        En Aktif Kullanıcılar (30 gün)
                      </div>
                      {topActive.length === 0 ? (
                        <div className={styles.emptyHint}>Son 30 günde giriş kaydı yok.</div>
                      ) : (
                        topActive.map((r, i) => (
                          <div key={r.emp.id} className={styles.rankingRow}>
                            <span className={styles.rankingOrder}>{i + 1}</span>
                            <span className={styles.avatar}>{initials(r.emp)}</span>
                            <span className={styles.rankingInfo}>
                              <span className={styles.rankingName}>{fullName(r.emp)}</span>
                              <span className={styles.rankingMeta}>
                                {r.logins30} giriş · {formatDuration(r.totalOnlineMs)}
                              </span>
                            </span>
                            {r.isOnline && <FaCircle className={styles.onlineDot} />}
                          </div>
                        ))
                      )}
                    </div>

                    <div className={styles.rankingCard}>
                      <div className={styles.rankingTitle}>
                        <FaBed className={styles.rankingIconLow} />
                        Aktif Olmayanlar (30 günde giriş yok)
                      </div>
                      {leastActive.length === 0 ? (
                        <div className={styles.emptyHint}>
                          Tüm kullanıcılar son 30 günde en az bir kez giriş yaptı.
                        </div>
                      ) : (
                        leastActive.map((r) => (
                          <div key={r.emp.id} className={styles.rankingRow}>
                            <span className={styles.avatar}>{initials(r.emp)}</span>
                            <span className={styles.rankingInfo}>
                              <span className={styles.rankingName}>{fullName(r.emp)}</span>
                              <span className={styles.rankingMeta}>
                                Son aktivite: {formatRelative(r.lastActivity)}
                              </span>
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}

              {activeTab === 'users' && (
                <div className={styles.tableWrap}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th className={styles.thSortable} onClick={() => toggleSort('name')}>
                          Kullanıcı {sortIcon('name')}
                        </th>
                        <th>Durum</th>
                        <th className={styles.thSortable} onClick={() => toggleSort('lastActivity')}>
                          Son Aktivite {sortIcon('lastActivity')}
                        </th>
                        <th className={styles.thSortable} onClick={() => toggleSort('logins30')}>
                          Giriş (30g) {sortIcon('logins30')}
                        </th>
                        <th className={styles.thSortable} onClick={() => toggleSort('totalLogins')}>
                          Toplam Giriş {sortIcon('totalLogins')}
                        </th>
                        <th className={styles.thSortable} onClick={() => toggleSort('totalOnlineMs')}>
                          Toplam Süre {sortIcon('totalOnlineMs')}
                        </th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map((r) => {
                        const expanded = expandedId === r.emp.id;
                        return (
                          <React.Fragment key={r.emp.id}>
                            <tr
                              className={styles.tr}
                              onClick={() => setExpandedId(expanded ? null : r.emp.id)}
                            >
                              <td>
                                <span className={styles.userCell}>
                                  <span className={styles.avatar}>{initials(r.emp)}</span>
                                  <span className={styles.userCellInfo}>
                                    <span className={styles.rankingName}>{fullName(r.emp)}</span>
                                    <span className={styles.rankingMeta}>{r.emp.eMail || '—'}</span>
                                  </span>
                                </span>
                              </td>
                              <td>
                                {r.isOnline ? (
                                  <span className={styles.statusOnline}>
                                    <FaCircle /> Çevrimiçi
                                    {r.currentSessionMs && (
                                      <small> ({formatDuration(r.currentSessionMs)})</small>
                                    )}
                                  </span>
                                ) : (
                                  <span className={styles.statusOffline}>Çevrimdışı</span>
                                )}
                              </td>
                              <td title={formatDateTime(r.lastActivity)}>
                                {formatRelative(r.lastActivity)}
                              </td>
                              <td className={styles.numCell}>{r.logins30}</td>
                              <td className={styles.numCell}>{r.totalLogins}</td>
                              <td className={styles.numCell}>{formatDuration(r.totalOnlineMs)}</td>
                              <td className={styles.chevronCell}>
                                {expanded ? <FaChevronUp /> : <FaChevronDown />}
                              </td>
                            </tr>
                            {expanded && (
                              <tr className={styles.detailRow}>
                                <td colSpan={7}>
                                  <div className={styles.detailGrid}>
                                    <div className={styles.detailStat}>
                                      <span className={styles.detailStatLabel}>Giriş (7 gün)</span>
                                      <span className={styles.detailStatValue}>{r.logins7}</span>
                                    </div>
                                    <div className={styles.detailStat}>
                                      <span className={styles.detailStatLabel}>Ort. Oturum</span>
                                      <span className={styles.detailStatValue}>
                                        {formatDuration(r.avgSessionMs)}
                                      </span>
                                    </div>
                                    <div className={styles.detailStat}>
                                      <span className={styles.detailStatLabel}>
                                        {r.isOnline ? 'Aktif Oturum' : 'Son Oturum'}
                                      </span>
                                      <span className={styles.detailStatValue}>
                                        {formatDuration(
                                          r.isOnline ? r.currentSessionMs : r.lastSessionMs
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                  <div className={styles.detailListTitle}>
                                    Son Girişler (30 gün içinde {r.logins30} kayıt)
                                  </div>
                                  {r.recent.length === 0 ? (
                                    <div className={styles.emptyHint}>
                                      Son 30 günde giriş kaydı bulunmuyor.
                                    </div>
                                  ) : (
                                    <div className={styles.loginList}>
                                      {r.recent.slice(0, 10).map((entry, idx) => {
                                        const device = entry.synthetic
                                          ? { label: 'Oturum kaydı', isMobile: false }
                                          : parseDevice(entry.device);
                                        return (
                                          <div key={idx} className={styles.loginItem}>
                                            {device.isMobile ? <FaMobileAlt /> : <FaDesktop />}
                                            <span>{formatDateTime(entry.ts)}</span>
                                            <span className={styles.loginDevice}>{device.label}</span>
                                          </div>
                                        );
                                      })}
                                      {r.recent.length > 10 && (
                                        <div className={styles.emptyHint}>
                                          + {r.recent.length - 10} giriş daha
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <p className={styles.footNote}>
                Giriş sayıları login geçmişi kayıtlarından, çevrimiçi süreler ise oturum takibinden
                hesaplanır. Süre takibi yeni etkinleştirildiyse geçmiş oturumlar toplamlara dahil
                değildir.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UsageStatsModal;
